/**
 * ============================================================================
 * EverythingAnswered — Mapper
 * ============================================================================
 * Purpose: Pure transform of CMS questionnaire → persona/question ViewModel.
 * ============================================================================
 */

import type {
  FaqPersona,
  FaqQuestion,
  FaqQuestionnairePersonaRaw,
} from "../model/everything-answered.types";

const IMAGE_MAP: Record<string, string> = {
  Student: "https://static.amityonline.com/_s/image_36_3_95ec0a178c.webp",
  Parent: "https://static.amityonline.com/_s/image_36_1_de46a37b7f.webp",
  Working: "https://static.amityonline.com/_s/image_36_1_1_ead82d691e.webp",
  Entrepreneur: "https://static.amityonline.com/_s/image_36_2_74414bec98.webp",
};

export function mapQuestionsMapping(
  questionsMapping: FaqQuestionnairePersonaRaw[] = []
): FaqPersona[] {
  return questionsMapping.map((persona) => {
    const seenPrimary = new Set<string>();
    const seenRelated = new Set<string>();
    const primaryRaw: FaqQuestionnairePersonaRaw["questions"] = [];
    const relatedRaw: NonNullable<
      FaqQuestionnairePersonaRaw["questions"][number]["questionnaire"]
    > = [];
    const relatedMap: Record<string, string[]> = {};

    persona.questions.forEach((q) => {
      if (!seenPrimary.has(q.documentId)) {
        seenPrimary.add(q.documentId);
        primaryRaw.push(q);
      }
      if (q.questionnaire && q.questionnaire.length > 0) {
        for (const related of q.questionnaire) {
          const rId = related.documentId;

          if (!seenPrimary.has(rId) && !seenRelated.has(rId)) {
            seenRelated.add(rId);
            relatedRaw.push(related);
          }

          (relatedMap[q.documentId] ??= []).push(rId);
          (relatedMap[rId] ??= []).push(q.documentId);
        }
      }
    });

    const primaryQuestions: FaqQuestion[] = primaryRaw.map((q, idx) => ({
      id: q.documentId,
      text: q.question,
      side: idx % 2 === 0 ? "right" : "left",
      borderColor: q.borderColour ?? null,
      answer: q.answer ?? null,
      relatedIds: relatedMap[q.documentId] ?? [],
    }));

    const relatedQuestions: FaqQuestion[] = relatedRaw.map((q) => ({
      id: q.documentId,
      text: q.question,
      side: null,
      borderColor: q.borderColour ?? null,
      answer: q.answer ?? null,
      relatedIds: relatedMap[q.documentId] ?? [],
    }));

    return {
      id: persona.Type.toLowerCase(),
      tab: persona.EnumHeading,
      image: IMAGE_MAP[persona.Type] || persona.image || "",
      EnumHeading: persona.EnumHeading,
      questions: primaryQuestions,
      allQuestions: [...primaryQuestions, ...relatedQuestions],
    };
  });
}
